<template>
  <div v-loading="loading">
    <el-card class="mt-15" shadow="never">
      <div slot="header" class="clearfix">
        <small>{{ $t('total_mentions_stats') }}</small>
        <el-button style="float:left" type="info" size="mini" @click="getStats(inbox)" :loading="waiting">
          <i class="fas fa-sync"></i> {{ $t('update') }}
        </el-button>
      </div>
      <el-row class="stats">
        <el-col :span="8" v-for="(stat, key) in stats" :key="key">
          <strong>{{ $t(key) }}</strong>: {{ stat }}
        </el-col>
      </el-row>
    </el-card>
    <ul v-if="inboxInfo !== null">
      <li>{{ $t('status') }}: {{ $t(inboxInfo.status) }}</li>
      <li>{{ $t('total_mentions') }}: {{ inboxInfo.mentions.stats.total }}</li>
      <li>
        <strong>
          <i class="fas fa-tags"></i>&nbsp;{{ $t('keywords') }}
        </strong>
        <ol>
          <li v-for="(pattern, i) in inboxInfo.rule.conditions.patterns" :key="`patter_${i}`">
            <small>{{ $t('main')}} : </small>
            <span class="main">{{ pattern.main_phrase.phrase_text }}</span>
            <small
              v-if="pattern.main_phrase.match_algorithm === 'exact'">
              ({{ $t('exact_match') }})
            </small>
            <small v-if="pattern.required_phrases.length > 0"> | &nbsp;{{ $t('required') }}: </small>
            <span class="required" v-for="(r, i) in pattern.required_phrases" :key="`r_${i}`">
              {{ r.phrase_text }}<small v-if="i < pattern.required_phrases.length - 1">، </small>
            </span>
            <small v-if="pattern.excluded_phrases.length > 0"> | &nbsp;{{ $t('excluded') }}: </small>
            <span class="excluded" v-for="(e, i) in pattern.excluded_phrases" :key="`e_${i}`">
              {{ e.phrase_text }}<small v-if="i < pattern.excluded_phrases.length - 1">، </small>
            </span>
          </li>
        </ol>
      </li>
      <li>
        <strong>
          <i class="fas fa-search"></i>&nbsp;{{ $t('media') }}:
        </strong>
        <span v-for="(m, i) in inboxInfo.rule.conditions.media" :key="`m_${i}`">
          {{ m.label }}<small v-if="i < inboxInfo.rule.conditions.media.length - 1">، </small>
        </span>
      </li>
      <li v-if="filters.length > 0">
        <strong>
          <i class="fa fa-project-diagram"></i>&nbsp;
          {{ $t('pipes') }}
        </strong>
        <ul>
          <li v-for="f in filters" :key="f.id.numberStr">
            {{ f.name }}
          </li>
        </ul>
      </li>
      <li v-if="inboxInfo.report_configurations.members.length > 0 || inboxInfo.report_configurations.types.length > 0">
        <strong>
          <i class="fa fa-envelope-open"></i>&nbsp;{{ $t('reports') }}
        </strong>
        <ul>
          <li>
            {{ $t('report_members') }}
            <ul v-if="inboxInfo.report_configurations.members.length > 0">
              <li v-for="(member, i) in inboxInfo.report_configurations.members" :key="`member_${i}`">
                {{ member.user.name }}
              </li>
            </ul>
            <span v-else>--</span>
          </li>
          <li>{{ $t('report_types') }}
            <ul v-if="inboxInfo.report_configurations.types.length > 0">
              <li v-for="(type, i) in inboxInfo.report_configurations.types" :key="`type_${i}`">
                {{ $t(type.name) }}
              </li>
            </ul>
            <span v-else>--</span>
          </li>
        </ul>
      </li>
    </ul>
  </div>
</template>

<script>
import { mapActions, mapGetters } from 'vuex'
import moment from 'moment'
export default {
  props: {
    inbox: {
      type: String,
      default: ''
    },
    path: {
      type: String,
      default: ''
    },
    team: {
      type: String,
      default: ''
    }
  },
  data () {
    return {
      stats: {
        last_24_hours: '--',
        last_48_hours: '--',
        last_7_days: '--'
      },
      inboxInfo: null,
      loading: false,
      waiting: false,
      filters: []
    }
  },
  computed: {
    ...mapGetters([
      'domainsPath'
    ]),
    customerID () {
      return this.$route.params.id
    }
  },
  methods: {
    ...mapActions([
      'handleRequest'
    ]),
    toggleLoading () {
      this.loading = !this.loading
    },
    toggleWaiting () {
      this.waiting = !this.waiting
    },
    setStatsData (res, key) {
      const { total } = res
      this.stats[key] = total
    },
    setStats (statistics, key) {
      const results = statistics
      const { axes, data } = results
      const columns = axes.find(axis => axis.title === 'columns').values
      const contentCountIndex = columns.indexOf('content_count')
      this.stats[key] = data.reduce((s, items) => {
        return items.reduce((accu, item) => {
          accu += item[contentCountIndex]
          return accu
        }, 0)
      }, 0)
    },
    async getStats (inbox) {
      const { path } = this
      const format = 'YYYY-MM-DD'
      const currentTime = moment()
      const last24Hours = currentTime.subtract(24, 'hours').format(format)
      const last48Hours = currentTime.subtract(48, 'hours').format(format)
      const last7Days = currentTime.subtract(7, 'days').format(format)
      const name = `${path}/${this.inbox}/activities`
      this.toggleWaiting()
      await this.handleRequest({
        name,
        action: 'getAll',
        data: {
          query: {
            date_start: last24Hours
          }
        }
      }).then(res => this.setStats(res, 'last_24_hours'))
      await this.handleRequest({
        name,
        action: 'getAll',
        data: {
          query: {
            date_start: last48Hours
          }
        }
      }).then(res => this.setStats(res, 'last_48_hours'))
      await this.handleRequest({
        name,
        action: 'getAll',
        data: {
          query: {
            date_start: last7Days
          }
        }
      })
        .then(res => this.setStats(res, 'last_7_days'))
        .finally(this.toggleWaiting)
    },
    async getData () {
      const { inbox, path } = this
      this.toggleLoading()
      await this.handleRequest({
        name: path,
        data: {
          id: inbox
        },
        action: 'getOne'
      }).then(res => {
        this.inboxInfo = res
      })
      await this.handleRequest({
        name: `${path}/${inbox}/filters`,
        action: 'getAll'
      }).then(filters => {
        this.filters = filters.list.filter(f => f.is_active)
      }).finally(this.toggleLoading)
    }
  },
  created () {
    this.getData()
    // this.getStats()
  }
}
</script>

<style lang="css">
</style>
